import { c as _c } from "react/compiler-runtime";
import { IcoCode224 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CopyButton from '../copy-button';
import { JsonEditor } from '../json-editor';
import SegmentedControl from '../segmented-control';
import VisualEditor from './components/visual-editor';
import validateSchema, { type ValidationError } from './utils/validate-schema';
const JSON_SOURCE_HEADER_HEIGHT = 41; // px-4 py-2 header + border
const TOOLBAR_HEIGHT = 52;
type SchemaEditorMode = 'simple' | 'advanced';
export type SchemaEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  onValidate?: (errors: ValidationError[], value: string) => void;
  height?: number;
  isDisabled?: boolean;
  /** Optional subtitle text shown in the empty state (e.g. "Define the data the agent will receive."). */
  emptyStateSubtitle?: string;
  title: ReactNode;
  subtitle?: ReactNode;
};
const SchemaEditor = t0 => {
  const $ = _c(57);
  const {
    value: t1,
    onChange,
    onValidate,
    height: t2,
    isDisabled: t3,
    emptyStateSubtitle,
    title,
    subtitle
  } = t0;
  const value = t1 === undefined ? "" : t1;
  const height = t2 === undefined ? 480 : t2;
  const isDisabled = t3 === undefined ? false : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keyPrefix: "components.schema-editor"
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t4);
  const [mode, setMode] = useState("simple");
  let t5;
  if ($[1] !== value) {
    t5 = validateSchema(value);
    $[1] = value;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const errors = t5;
  const hasErrors = errors.length > 0;
  let t6;
  if ($[3] !== errors || $[4] !== onValidate || $[5] !== value) {
    t6 = () => {
      onValidate?.(errors, value);
    };
    $[3] = errors;
    $[4] = onValidate;
    $[5] = value;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== errors || $[8] !== onValidate) {
    t7 = [errors, onValidate];
    $[7] = errors;
    $[8] = onValidate;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] !== height) {
    t8 = {
      height
    };
    $[10] = height;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== title) {
    t9 = <p className="text-label-2 text-primary">{title}</p>;
    $[12] = title;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== subtitle) {
    t10 = subtitle && <p className="text-body-2 text-tertiary">{subtitle}</p>;
    $[14] = subtitle;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div className="flex flex-1 flex-col gap-1">{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t) {
    t12 = t("mode.aria-label");
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = v => setMode(v as SchemaEditorMode);
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t) {
    t14 = t("mode.simple");
    $[22] = t;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14) {
    t15 = {
      value: "simple",
      label: t14
    };
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t) {
    t16 = t("mode.advanced");
    $[26] = t;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t16) {
    t17 = {
      value: "advanced",
      label: t16
    };
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t15 || $[31] !== t17) {
    t18 = [t15, t17];
    $[30] = t15;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== mode || $[34] !== t12 || $[35] !== t18) {
    t19 = <SegmentedControl aria-label={t12} value={mode} onChange={t13} options={t18} size="compact" />;
    $[33] = mode;
    $[34] = t12;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t11 || $[38] !== t19) {
    t20 = <div className="flex shrink-0 items-center justify-between gap-4">{t11}{t19}</div>;
    $[37] = t11;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = hasErrors ? "border-error" : "border-tertiary";
  let t22;
  if ($[40] !== t21) {
    t22 = cx("flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border bg-primary", t21);
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== emptyStateSubtitle || $[43] !== height || $[44] !== isDisabled || $[45] !== mode || $[46] !== onChange || $[47] !== t || $[48] !== value) {
    t23 = mode === "simple" ? <VisualEditor value={value} onChange={onChange || _temp} isDisabled={isDisabled} emptyStateSubtitle={emptyStateSubtitle} /> : <><div className="flex shrink-0 items-center justify-between border-b border-tertiary px-4 py-2"><div className="flex items-center gap-2"><IcoCode224 /><p className="text-label-2 text-secondary">{t("advanced.title")}</p></div><CopyButton contentToCopy={value} ariaLabel={t("advanced.copy-aria-label")} /></div><div className="min-h-0 flex-1 overflow-hidden [&_.rounded-md]:rounded-none"><JsonEditor value={value} onChange={onChange} height={height - TOOLBAR_HEIGHT - JSON_SOURCE_HEADER_HEIGHT} isDisabled={isDisabled} /></div></>;
    $[42] = emptyStateSubtitle;
    $[43] = height;
    $[44] = isDisabled;
    $[45] = mode;
    $[46] = onChange;
    $[47] = t;
    $[48] = value;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <div className={t22}>{t23}</div>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t20 || $[54] !== t24 || $[55] !== t8) {
    t25 = <div className="flex w-full flex-col gap-3" style={t8}>{t20}{t24}</div>;
    $[53] = t20;
    $[54] = t24;
    $[55] = t8;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  return t25;
};
export default SchemaEditor;
function _temp() {}